import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
    Box, Button, Container, Flex, Grid, GridItem, Heading, HStack, Image, Text, VStack,
    SimpleGrid, Badge, Separator, useBreakpointValue, Icon, Spinner, Link, } from '@chakra-ui/react'
import { toaster } from "../components/ui/toaster";
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { FaMapMarkerAlt, FaStar, FaArrowRight, FaSearch } from 'react-icons/fa'
import 'leaflet/dist/leaflet.css'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'
import { useSearchBarberShopsQuery } from '../services/publicService'
import { SEO } from '../components/SEO'
import { BarberPole3D } from '../components/BarberPole3D'
import { analytics } from '../utils/analytics'
import { IMAGE_URL } from '../constants/API'

// ─── Design tokens ──────────────────────────────────────────────
const BLUE = '#0088FF'
const BLUE_600 = '#006DD6'
const BLUE_900 = '#00203D'
const RED = '#FF3D3D'
const GREEN = '#22C55E'
const MONO = "'Space Mono', monospace"

// ─── Scroll Reveal ──────────────────────────────────────────────
const MotionBox = motion(Box)
const ScrollReveal = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { once: true, margin: '-60px' })
    // Safety valve for in-app WebViews (Instagram iOS) where IntersectionObserver
    // timing is flaky: if the reveal hasn't fired a few seconds after mount,
    // force it — an un-animated section beats an invisible page.
    const [forced, setForced] = useState(false)
    useEffect(() => {
        const id = window.setTimeout(() => setForced(true), 4000)
        return () => window.clearTimeout(id)
    }, [])
    return (
        <MotionBox ref={ref} initial={{ opacity: 0, y: 24 }} animate={(inView || forced) ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay, ease: [0.2, 0.8, 0.2, 1] }}>
            {children}
        </MotionBox>
    )
}

// ─── Leaflet icons ──────────────────────────────────────────────
const shopMarkerIcon = new L.DivIcon({
    className: 'bm-shop-marker',
    html: `<div style="width:12px;height:12px;border-radius:50%;background:#FF3D3D;box-shadow:0 0 0 3px white,0 2px 8px rgba(255,61,61,.4);position:absolute;left:50%;top:50%;transform:translate(-50%,-50%)"></div>`,
    iconSize: [16, 16], iconAnchor: [8, 8], popupAnchor: [0, -12],
})
const userMarkerIcon = new L.DivIcon({
    className: 'bm-user-marker',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:#0088FF;box-shadow:0 0 0 3px white,0 0 0 5px #0088FF;position:absolute;left:50%;top:50%;transform:translate(-50%,-50%)"></div>`,
    iconSize: [22, 22], iconAnchor: [11, 11], popupAnchor: [0, -14],
})
const FlyToPos = ({ pos }: { pos: [number, number] | null }) => { const m = useMap(); useEffect(() => { if (pos) m.flyTo(pos, 14, { duration: 1.2 }) }, [pos, m]); return null }

// ─── Map Section (real data from API) ───────────────────────────
const MAP_CENTERS: Record<string, [number, number]> = {
    da: [56.1572, 10.2107], // Aarhus, DK
    default: [49.2236, 18.7394], // Žilina, SK
}

const MapSection = ({ t }: { t: any }) => {
    const { i18n } = useTranslation()
const { data: apiResponse, isLoading } = useSearchBarberShopsQuery({ search: '', page: 1, limit: 50 })
    const shops = apiResponse?.data ?? []
    const [userPos, setUserPos] = useState<[number, number] | null>(null)
    const [locating, setLocating] = useState(false)
    const mapCenter = MAP_CENTERS[i18n.language?.split('-')[0] ?? ''] ?? MAP_CENTERS.default

    const shopsWithCoords = useMemo(() => shops.filter(s => s.address?.latitude && s.address?.longitude), [shops])

    const findMe = () => {
        if (!navigator.geolocation) return
        setLocating(true)
        navigator.geolocation.getCurrentPosition(
            pos => { setUserPos([pos.coords.latitude, pos.coords.longitude]); setLocating(false); toaster.create({ title: t('landing.map.locationFound'), type: 'success', duration: 3000 }) },
            () => { setLocating(false); toaster.create({ title: t('landing.map.locationError'), description: t('landing.map.enableLocation'), type: 'error', duration: 5000 }) },
            { timeout: 8000 }
        )
    }

    return (
        <Box as="section" bg="white" py={{ base: 20, md: 32 }} px={{ base: 4, md: 8 }}>
            <Container maxW="1280px">
                <ScrollReveal>
                    <Flex justify="space-between" align="flex-end" mb={10} flexWrap="wrap" gap={4}>
                        <Box>
                            <Text fontFamily={MONO} fontSize="11px" letterSpacing="0.3em" color={BLUE} mb={3}>◆ {t('landing.map.coverage')}</Text>
                            <Heading as="h2" fontSize={{ base: '36px', md: '56px' }} letterSpacing="-0.02em" lineHeight={1.05} color="gray.900">{t('landing.map.title')}</Heading>
                            <Text fontSize="18px" color="gray.600" mt={3} maxW="500px">{t('landing.map.sub')}</Text>
                        </Box>
                        <HStack gap={3} flexWrap="wrap">
                            <Badge bg="rgba(34,197,94,0.1)" color={GREEN} px={3} py={1.5} borderRadius="full" fontFamily={MONO} fontSize="10px" letterSpacing="0.15em" fontWeight={700}>
                                <HStack gap={2}><Box w="6px" h="6px" borderRadius="full" bg={GREEN} /><Text>{t('landing.map.freeForCustomers')}</Text></HStack>
                            </Badge>
                            <Button asChild bg={BLUE} color="white" fontFamily={MONO} fontSize="11px" letterSpacing="0.2em" fontWeight={700} borderRadius="md" px={5}
                                _hover={{ bg: BLUE_600, transform: 'translateY(-2px)' }} boxShadow="0 4px 12px rgba(0,136,255,.3)">
                                <RouterLink to="/explore">{t('landing.map.exploreAll').toUpperCase()} <Icon as={FaArrowRight} ml={2} /></RouterLink>
                            </Button>
                        </HStack>
                    </Flex>
                </ScrollReveal>

                <ScrollReveal delay={0.1}>
                    <Box borderRadius="2xl" overflow="hidden" boxShadow="xl" border="1px solid" borderColor="gray.200" position="relative">
                        {isLoading && (
                            <Flex position="absolute" inset={0} zIndex={1000} bg="whiteAlpha.800" align="center" justify="center">
                                <Spinner size="xl" color={BLUE} borderWidth="3px" />
                            </Flex>
                        )}
                        <Box h={{ base: '350px', md: '500px' }}>
                            <MapContainer center={mapCenter} zoom={13} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }} attributionControl={false}>
                                <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
                                <FlyToPos pos={userPos} />
                                {userPos && <Marker position={userPos} icon={userMarkerIcon}><Popup>{t('landing.map.yourLocation')}</Popup></Marker>}
                                {shopsWithCoords.map(s => (
                                    <Marker key={s.id} position={[s.address!.latitude!, s.address!.longitude!]} icon={shopMarkerIcon}>
                                        <Popup>
                                            <Box p={1} minW="200px" fontFamily="Inter, sans-serif">
                                                <Text fontWeight={700} fontSize="14px" color="gray.900" mb="2px">{s.name}</Text>
                                                {s.address?.city && <Text fontSize="12px" color="gray.500" mb={1}>{s.address.city}</Text>}
                                                <HStack fontSize="12px" mb={2} gap={2}>
                                                    {s.average_rating !== null && <><Icon as={FaStar} color="#FBBF24" boxSize="12px" /><Text fontWeight={600}>{s.average_rating}</Text></>}
                                                    {s.review_count > 0 && <Text color="gray.500">({s.review_count})</Text>}
                                                </HStack>
                                                <Button asChild onClick={() => analytics.shopCardClicked(s.slug, 0, 'landing')} bg={BLUE} color="white" size="sm" w="full" borderRadius="8px" fontSize="12px" fontWeight={600}>
                                                    <RouterLink to={`/r/${s.slug}`}>{t('landing.map.bookNow')} →</RouterLink>
                                                </Button>
                                            </Box>
                                        </Popup>
                                    </Marker>
                                ))}
                            </MapContainer>
                        </Box>
                        {/* Locate button */}
                        <Button
                            onClick={findMe} loading={locating}
                            position="absolute" top={4} left={4} zIndex={1000}
                            bg="white" color="gray.900" border="1px solid" borderColor="gray.200"
                            borderRadius="lg" boxShadow="md" px={4} py={2} h="auto"
                            fontFamily={MONO} fontSize="11px" letterSpacing="0.15em" fontWeight={700}
                            _hover={{ boxShadow: 'lg' }}
                        >
                            <Icon as={FaMapMarkerAlt} color={BLUE} mr={2} />
                            {t('landing.map.findNearMe')}
                        </Button>
                        {/* Explore CTA overlay */}
                        <Flex position="absolute" bottom={4} left={4} right={4} zIndex={1000} justify="center">
                            <Button asChild
                                bg="gray.900" color="white" borderRadius="full" px={6} py={3} h="auto"
                                fontFamily={MONO} fontSize="12px" letterSpacing="0.15em" fontWeight={700}
                                boxShadow="0 8px 24px rgba(0,0,0,0.25)"
                                _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 32px rgba(0,0,0,0.3)' }}
                            >
                                <RouterLink to="/explore"><Icon as={FaSearch} mr={2} />{t('landing.map.exploreAll').toUpperCase()} →</RouterLink>
                            </Button>
                        </Flex>
                    </Box>
                </ScrollReveal>

                {/* Quick shop cards below map */}
                {shopsWithCoords.length > 0 && (
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={4} mt={8}>
                        {shopsWithCoords.slice(0, 3).map((s, idx) => (
                            <ScrollReveal key={s.id}>
                                <RouterLink to={`/r/${s.slug}`} onClick={() => analytics.shopCardClicked(s.slug, idx + 1, 'landing')} style={{ textDecoration: 'none', display: 'block' }}>
                                <Box bg="gray.50" borderRadius="xl" p={5} border="1px solid" borderColor="gray.200"
                                    _hover={{ boxShadow: 'md', transform: 'translateY(-2px)', borderColor: BLUE }} transition="all .2s"
                                >
                                    <HStack gap={3} mb={2}>
                                        {s.logo ? (
                                            <Image src={`${IMAGE_URL}/${s.logo}`} alt={s.name} boxSize="40px" borderRadius="lg" objectFit="cover" flexShrink={0} />
                                        ) : (
                                            <Flex w="40px" h="40px" borderRadius="lg" bg={s.primary_color || BLUE} color="white" align="center" justify="center" fontWeight={700} fontSize="14px" flexShrink={0}>
                                                {s.name.split(' ').slice(0, 2).map(w => w[0]).join('')}
                                            </Flex>
                                        )}
                                        <Box>
                                            <Text fontWeight={700} fontSize="15px" color="gray.900">{s.name}</Text>
                                            {s.address?.city && <Text fontSize="12px" color="gray.500">{s.address.city}</Text>}
                                        </Box>
                                    </HStack>
                                    <HStack fontSize="12px" color="gray.600" gap={2}>
                                        {s.average_rating !== null && <><Icon as={FaStar} color="#FBBF24" boxSize="12px" /><Text fontWeight={600}>{s.average_rating}</Text></>}
                                        {s.services.length > 0 && <Text>{s.average_rating !== null && '· '}{s.services.slice(0, 2).map(sv => sv.name).join(', ')}</Text>}
                                    </HStack>
                                </Box>
                                </RouterLink>
                            </ScrollReveal>
                        ))}
                    </SimpleGrid>
                )}
            </Container>
        </Box>
    )
}

// ─── Explore CTA Banner (for customers) ─────────────────────────
const ExploreBanner = ({ t }: { t: any }) => (
    <Box backgroundImage={`linear-gradient(135deg, ${BLUE}, ${BLUE_600})`} color="white" py={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }} position="relative" overflow="hidden">
        <Box position="absolute" inset={0} bgImage="radial-gradient(circle, rgba(255,255,255,.1) 1px, transparent 1px)" bgSize="24px 24px" />
        <Container maxW="1100px" position="relative" textAlign="center">
            <ScrollReveal>
                <Heading as="h2" fontSize={{ base: '32px', md: '48px' }} fontWeight={700} letterSpacing="-0.02em" mb={4}>
                    {t('landing.exploreBanner.title')}
                </Heading>
                <Text fontSize="18px" opacity={0.9} mb={8} maxW="600px" mx="auto">
                    {t('landing.hero.subCustomers')}
                </Text>
                <HStack gap={4} justify="center" flexWrap="wrap">
                    <Button asChild bg="white" color={BLUE}
                        fontFamily={MONO} fontWeight={700} fontSize="13px" letterSpacing="0.2em" px={8} py={5} h="auto" borderRadius="md"
                        _hover={{ transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
                    >
                        <RouterLink to="/explore"><Icon as={FaSearch} mr={2} />{t('landing.exploreBanner.cta').toUpperCase()} →</RouterLink>
                    </Button>
                    <Button asChild variant="outline" borderColor="whiteAlpha.500" color="white"
                        fontFamily={MONO} fontWeight={700} fontSize="13px" letterSpacing="0.2em" px={8} py={5} h="auto" borderRadius="md"
                        _hover={{ bg: 'whiteAlpha.100', borderColor: 'whiteAlpha.700' }}
                    >
                        <RouterLink to="/register">{t('landing.exploreBanner.register').toUpperCase()}</RouterLink>
                    </Button>
                </HStack>
            </ScrollReveal>
        </Container>
    </Box>
)

// ─── Booking Demo (v2: white card, rounded, blue accents) ──────
type DemoService = { name: string; price: string; dur: string }
const BARBERS = ['Marek', 'Tomáš', 'Lukas']
const TIMES = ['10:00', '10:30', '11:00', '11:30', '13:00', '13:30', '14:00', '14:30']
const STEP_LABELS = ['Service', 'Barber', 'Date', 'Time', 'Confirm', 'Booked']

const BookingDemo = ({ t }: { t: any }) => {
    const [step, setStep] = useState(0)
    const [svc, setSvc] = useState<number | null>(null)
    const [barber, setBarber] = useState<number | null>(null)
    const [time, setTime] = useState<number | null>(null)
    const reset = () => { setStep(0); setSvc(null); setBarber(null); setTime(null) }
    const stepLabels: string[] = t('landing.demo.stepLabels', { returnObjects: true })
    const services: DemoService[] = t('landing.demo.services', { returnObjects: true })
    const dates: string[] = t('landing.demo.dates', { returnObjects: true })

    return (
        <Box position="relative" bg="white" borderRadius="2xl" boxShadow="xl" border="1px solid" borderColor="gray.200" overflow="hidden" maxW="420px" mx={{ base: 'auto', lg: 0 }} ml={{ lg: 'auto' }}>
            {/* DEMO stamp */}
            <Badge
                position="absolute" top={3} right={3} bg={RED} color="white"
                px={3} py={1.5} borderRadius="md"
                fontFamily={MONO} fontSize="9px" fontWeight={700} letterSpacing="0.2em"
                transform="rotate(3deg)" zIndex={2}
            >
                {t('landing.hero.demoBadge')}
            </Badge>

            {/* Header */}
            <Flex justify="space-between" align="center" px={5} py={4} borderBottom="1px solid" borderColor="gray.100">
                <Text fontFamily={MONO} fontSize="10px" letterSpacing="0.2em" color="gray.500">
                    {t('landing.demo.step').toUpperCase()} {step + 1}/6 · {(stepLabels[step] || STEP_LABELS[step]).toUpperCase()}
                </Text>
            </Flex>

            <Box p={5} minH="380px">
                {/* Step 0: Service */}
                {step === 0 && (
                    <VStack align="stretch" gap={3}>
                        {services.map((s, i) => (
                            <Box key={s.name} as="button" onClick={() => { setSvc(i); setStep(1) }}
                                p={4} borderRadius="lg" border="1.5px solid" borderColor={svc === i ? BLUE : 'gray.200'}
                                textAlign="left" _hover={{ borderColor: '#3DA9FF', bg: '#F0F7FF' }} transition="all .15s"
                            >
                                <Flex justify="space-between" align="center">
                                    <VStack align="flex-start" gap={0}>
                                        <Text fontWeight={600} color="gray.900">{s.name}</Text>
                                        <Text fontFamily={MONO} fontSize="11px" color="gray.500">{s.dur}</Text>
                                    </VStack>
                                    <Text fontWeight={700} color={BLUE}>{s.price}</Text>
                                </Flex>
                            </Box>
                        ))}
                    </VStack>
                )}

                {/* Step 1: Barber */}
                {step === 1 && (
                    <SimpleGrid columns={3} gap={3}>
                        {BARBERS.map((b, i) => (
                            <VStack key={b} as="button" onClick={() => { setBarber(i); setStep(2) }}
                                p={4} borderRadius="lg" border="1.5px solid" borderColor={barber === i ? BLUE : 'gray.200'}
                                _hover={{ borderColor: '#3DA9FF' }}
                            >
                                <Box w="48px" h="48px" borderRadius="full" bg="gray.200" />
                                <Text fontWeight={600} fontSize="14px" color="gray.900">{b}</Text>
                            </VStack>
                        ))}
                    </SimpleGrid>
                )}

                {/* Step 2: Date */}
                {step === 2 && (
                    <VStack align="stretch" gap={3}>
                        {dates.map((d) => (
                            <Box key={d} as="button" onClick={() => setStep(3)}
                                p={4} borderRadius="lg" border="1.5px solid" borderColor="gray.200"
                                _hover={{ borderColor: '#3DA9FF' }} textAlign="left" fontWeight={600} color="gray.900"
                            >{d}</Box>
                        ))}
                    </VStack>
                )}

                {/* Step 3: Time */}
                {step === 3 && (
                    <SimpleGrid columns={4} gap={2}>
                        {TIMES.map((tm, i) => (
                            <Box key={tm} as="button" onClick={() => { setTime(i); setStep(4) }}
                                py={3} borderRadius="md" border="1.5px solid" borderColor={time === i ? BLUE : 'gray.200'}
                                _hover={{ borderColor: '#3DA9FF' }}
                                fontFamily={MONO} fontSize="13px" fontWeight={600} color="gray.900"
                            >{tm}</Box>
                        ))}
                    </SimpleGrid>
                )}

                {/* Step 4: Confirm */}
                {step === 4 && (
                    <VStack align="stretch" gap={4}>
                        <Box p={4} bg="gray.50" borderRadius="lg">
                            <Text fontFamily={MONO} fontSize="10px" color="gray.500" letterSpacing="0.2em">{t('landing.demo.review').toUpperCase()}</Text>
                            <Text fontWeight={600} color="gray.900" mt={2}>{services[svc ?? 0].name}</Text>
                            <Text fontSize="14px" color="gray.600">{t('landing.demo.with')} {BARBERS[barber ?? 0]} · {TIMES[time ?? 0]}</Text>
                        </Box>
                        <Button bg={BLUE} color="white" w="full" fontFamily={MONO} fontWeight={700} fontSize="11px" letterSpacing="0.2em" borderRadius="md" _hover={{ bg: BLUE_600, transform: 'translateY(-2px)' }} boxShadow="0 4px 12px rgba(0,136,255,.3)" onClick={() => setStep(5)}>
                            {t('landing.demo.confirm').toUpperCase()} →
                        </Button>
                    </VStack>
                )}

                {/* Step 5: Booked */}
                {step === 5 && (
                    <VStack gap={5} pt={4} textAlign="center">
                        <Flex w="64px" h="64px" borderRadius="full" bg={GREEN} color="white" align="center" justify="center" fontSize="32px">✓</Flex>
                        <Heading fontSize="28px" fontWeight={700} color="gray.900">{t('landing.demo.successTitle')}</Heading>
                        <Text fontSize="14px" color="gray.600">{t('landing.demo.successSub')}</Text>
                        <Button asChild w="full" bg={BLUE} color="white" fontFamily={MONO} fontWeight={700} fontSize="11px" letterSpacing="0.2em" borderRadius="md" _hover={{ bg: BLUE_600, transform: 'translateY(-2px)' }} boxShadow="0 4px 12px rgba(0,136,255,.3)">
                            <RouterLink to="/explore">{t('landing.hero.findRealShop').toUpperCase()}</RouterLink>
                        </Button>
                        <Box as="button" onClick={reset} fontFamily={MONO} fontSize="10px" letterSpacing="0.2em" color="gray.500" _hover={{ color: BLUE }}>
                            ↺ {t('landing.demo.tryAgain').toUpperCase()}
                        </Box>
                    </VStack>
                )}
            </Box>
        </Box>
    )
}

// ─── How It Works (v2: navy bg, rounded cards) ──────────────────
const HowItWorks = ({ t }: { t: any }) => {
    const steps = t('landing.howItWorks.steps', { returnObjects: true }) as Array<{ n: string; title: string; desc: string }>
    return (
        <Box as="section" id="how-it-works" bg={BLUE_900} color="white" py={{ base: 20, md: 32 }} px={{ base: 4, md: 8 }}>
            <Container maxW="1280px">
                <ScrollReveal>
                    <VStack align="flex-start" gap={4} mb={16}>
                        <Text fontFamily={MONO} fontSize="11px" letterSpacing="0.3em" color="#84C3FF">
                            ◆ {t('landing.howItWorks.eyebrow')}
                        </Text>
                        <Heading as="h2" fontSize={{ base: '40px', md: '64px' }} letterSpacing="-0.02em" lineHeight={1.05} maxW="800px">
                            {t('landing.howItWorks.title')}
                        </Heading>
                    </VStack>
                </ScrollReveal>

                <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: 8, md: 10 }}>
                    {steps.map((s, i) => (
                        <ScrollReveal key={s.n} delay={i * 0.1}>
                            <VStack align="flex-start" gap={4} p={8} bg="whiteAlpha.50" borderRadius="xl" border="1px solid" borderColor="whiteAlpha.100" h="full">
                                <Text fontWeight={700} fontSize="72px" color={RED} lineHeight={1}>{s.n}</Text>
                                <Heading as="h3" fontSize="24px" fontWeight={700}>{s.title}</Heading>
                                <Text color="whiteAlpha.700" lineHeight={1.6}>{s.desc}</Text>
                            </VStack>
                        </ScrollReveal>
                    ))}
                </SimpleGrid>
            </Container>
        </Box>
    )
}

// ─── Pricing (v2: rounded cards, featured dark card) ────────────
type Plan = { name: string; tagline: string; price: string; period: string; features: string[]; cta: string; featured?: boolean; ribbon?: string }

const PricingSection = ({ t }: { t: any }) => {
    const plans = t('landing.pricing.plans', { returnObjects: true }) as Plan[]
    return (
        <Box as="section" id="pricing" bg="gray.50" py={{ base: 20, md: 32 }} px={{ base: 4, md: 8 }}>
            <Container maxW="1280px">
                <ScrollReveal>
                    <VStack align="flex-start" gap={4} mb={16}>
                        <Text fontFamily={MONO} fontSize="11px" letterSpacing="0.3em" color={RED}>
                            ◆ {t('landing.pricing.eyebrow')}
                        </Text>
                        <Heading as="h2" fontSize={{ base: '40px', md: '64px' }} letterSpacing="-0.02em" lineHeight={1.05} color="gray.900">
                            {t('landing.pricing.title')}
                        </Heading>
                        <Text fontSize="18px" color="gray.600" maxW="600px">{t('landing.pricing.sub')}</Text>
                    </VStack>
                </ScrollReveal>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
                    {plans.map((p, i) => (
                        <ScrollReveal key={p.name} delay={i * 0.1}>
                            <PriceCard plan={p} />
                        </ScrollReveal>
                    ))}
                </SimpleGrid>

                <ScrollReveal delay={0.3}>
                    <Flex justify="center" mt={10}>
                        <Button
                            asChild
                            variant="outline" size="lg"
                            color={BLUE} borderColor={BLUE}
                            fontFamily={MONO} fontWeight={700} fontSize="11px" letterSpacing="0.2em"
                            borderRadius="md"
                            _hover={{ bg: BLUE, color: 'white' }}
                        >
                            <RouterLink to="/pricing">{t('landing.pricing.seeAll').toUpperCase()} →</RouterLink>
                        </Button>
                    </Flex>
                </ScrollReveal>
            </Container>
        </Box>
    )
}

const PriceCard = ({ plan }: { plan: Plan }) => {
    const featured = plan.featured
    return (
        <Box
            position="relative" bg={featured ? 'gray.900' : 'white'} color={featured ? 'gray.50' : 'gray.900'}
            p={8} borderRadius="2xl" border="1px solid" borderColor={featured ? 'gray.900' : 'gray.200'}
            boxShadow={featured ? 'xl' : 'md'} transform={{ md: featured ? 'translateY(-8px)' : 'none' }}
            _hover={{ boxShadow: 'xl', transform: featured ? { md: 'translateY(-12px)' } : 'translateY(-4px)' }}
            transition="all 0.2s"
        >
            {plan.ribbon && (
                <Badge position="absolute" top={-3} left={6} bg={RED} color="white" px={3} py={1} borderRadius="full" fontFamily={MONO} fontSize="10px" letterSpacing="0.2em">
                    {plan.ribbon}
                </Badge>
            )}
            <VStack align="flex-start" gap={5} h="full">
                <VStack align="flex-start" gap={1}>
                    <Text fontFamily={MONO} fontSize="11px" letterSpacing="0.2em" color={featured ? '#84C3FF' : BLUE}>{plan.name}</Text>
                    <Text fontSize="14px" color={featured ? 'whiteAlpha.700' : 'gray.500'}>{plan.tagline}</Text>
                </VStack>
                <HStack align="baseline" gap={1}>
                    <Heading as="span" fontSize="56px" fontWeight={700} letterSpacing="-0.02em">{plan.price}</Heading>
                    <Text fontSize="16px" color={featured ? 'whiteAlpha.700' : 'gray.500'}>{plan.period}</Text>
                </HStack>
                <VStack align="flex-start" gap={3} w="full" flex={1}>
                    {plan.features.map((f) => (
                        <HStack key={f} gap={3}>
                            <Text color={GREEN} fontWeight={700}>✓</Text>
                            <Text fontSize="15px" color={featured ? 'whiteAlpha.900' : 'gray.700'}>{f}</Text>
                        </HStack>
                    ))}
                </VStack>
                <Button
                    asChild
                    w="full" mt={2}
                    bg={featured ? RED : BLUE}
                    color="white"
                    fontFamily={MONO} fontWeight={700} fontSize="11px" letterSpacing="0.2em"
                    borderRadius="md"
                    _hover={{ bg: featured ? '#E61F1F' : BLUE_600, transform: 'translateY(-2px)' }}
                    boxShadow={featured ? '0 4px 12px rgba(255,61,61,.3)' : '0 4px 12px rgba(0,136,255,.3)'}
                >
                    <RouterLink to="/register">{plan.cta.toUpperCase()} →</RouterLink>
                </Button>
            </VStack>
        </Box>
    )
}

// ─── Final CTA (v2: navy bg, BM watermark) ──────────────────────
const FinalCTA = ({ t }: { t: any }) => {
    const lines: string[] = t('landing.finalCta.title', { returnObjects: true })
    return (
        <Box as="section" bg={BLUE_900} color="white" py={{ base: 24, md: 40 }} px={{ base: 4, md: 8 }} position="relative" overflow="hidden">
            <Text
                position="absolute" bottom="-40px" left="50%" transform="translateX(-50%)"
                fontWeight={800} fontSize={{ base: '200px', md: '400px' }}
                color="whiteAlpha.50" letterSpacing="-0.05em"
                pointerEvents="none" userSelect="none"
            >BM</Text>

            <Container maxW="1100px" position="relative" textAlign="center">
                <ScrollReveal>
                    <Heading as="h2" fontSize={{ base: '56px', md: '120px' }} fontWeight={800} letterSpacing="-0.03em" lineHeight={0.9} mb={8}>
                        {lines.map((l, i) => (
                            <Box as="span" key={i} display="block" color={i === lines.length - 1 ? RED : 'inherit'}>{l}</Box>
                        ))}
                    </Heading>
                    <Text fontSize="18px" color="whiteAlpha.700" mb={10}>{t('landing.finalCta.sub')}</Text>
                    <HStack gap={4} justify="center" flexWrap="wrap">
                        <Button asChild bg="white" color="gray.900"
                            fontFamily={MONO} fontWeight={700} fontSize="13px" letterSpacing="0.2em" px={8} py={5} h="auto" borderRadius="md"
                            _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                            boxShadow="0 8px 24px rgba(0,0,0,0.25)"
                        >
                            <RouterLink to="/register">{t('landing.finalCta.primary').toUpperCase()} →</RouterLink>
                        </Button>
                        <Button bg="transparent" color="white" border="1px solid" borderColor="whiteAlpha.400"
                            fontFamily={MONO} fontWeight={700} fontSize="13px" letterSpacing="0.2em" px={8} py={5} h="auto" borderRadius="md"
                            _hover={{ borderColor: 'whiteAlpha.700', bg: 'whiteAlpha.100' }}
                        >
                            {t('landing.finalCta.secondary').toUpperCase()}
                        </Button>
                    </HStack>
                </ScrollReveal>
            </Container>
        </Box>
    )
}

// ─── Footer (v2: gray.900 bg, clean columns, real links) ────────
const FOOTER_LINKS: Record<string, Record<string, string>> = {
    Product: { Features: '/#how-it-works', Pricing: '/pricing', Explore: '/explore', Funkcie: '/#how-it-works', 'Cenník': '/pricing', Objavuj: '/explore' },
    Company: { About: '/about', Contact: 'mailto:support@barbermate.eu', 'O nás': '/about', Kontakt: 'mailto:support@barbermate.eu' },
    Legal: { Privacy: '/privacy', Terms: '/terms', Cookies: '/cookies', GDPR: '/privacy', 'Súkromie': '/privacy', Podmienky: '/terms' },
    Produkt: { Funkcie: '/#how-it-works', 'Cenník': '/pricing', Objavuj: '/explore' },
    'Spoločnosť': { 'O nás': '/about', Kontakt: 'mailto:support@barbermate.eu' },
    'Právne': { 'Súkromie': '/privacy', Podmienky: '/terms', Cookies: '/cookies', GDPR: '/privacy' },
}

const LandingFooter = ({ t }: { t: any }) => {
    const cols = t('landing.footer.cols', { returnObjects: true }) as Array<{ h: string; links: string[] }>
    return (
        <Box as="footer" bg="gray.900" color="gray.400" py={16} px={{ base: 4, md: 8 }}>
            <Container maxW="1280px">
                <SimpleGrid columns={{ base: 1, md: 4 }} gap={10} mb={12}>
                    <VStack align="flex-start" gap={4}>
                        <Link asChild fontWeight={700} fontSize="24px" color="white" letterSpacing="-0.04em" _hover={{ textDecoration: 'none' }}>
                            <RouterLink to="/">BARBER<Text as="span" color={RED}>MATE</Text></RouterLink>
                        </Link>
                        <Text fontSize="14px" lineHeight={1.6} maxW="280px">{t('landing.footer.tag')}</Text>
                    </VStack>
                    {cols.map((c) => (
                        <VStack key={c.h} align="flex-start" gap={3}>
                            <Text fontFamily={MONO} fontSize="11px" letterSpacing="0.2em" color="white" textTransform="uppercase">{c.h}</Text>
                            {c.links.map((label) => {
                                const href = FOOTER_LINKS[c.h]?.[label] || '/'
                                const isExternal = href.startsWith('mailto:')
                                return isExternal ? (
                                    <Link key={label} href={href} onClick={() => analytics.contactClicked('email')} fontSize="14px" cursor="pointer" _hover={{ color: '#84C3FF' }} transition="color 0.15s">{label}</Link>
                                ) : (
                                    <Link key={label} asChild fontSize="14px" cursor="pointer" _hover={{ color: '#84C3FF' }} transition="color 0.15s" display="block">
                                        <RouterLink to={href}>{label}</RouterLink>
                                    </Link>
                                )
                            })}
                        </VStack>
                    ))}
                </SimpleGrid>
                <Separator borderColor="whiteAlpha.200" />
                <Flex justify="space-between" pt={6} fontFamily={MONO} fontSize="10px" letterSpacing="0.2em" textTransform="uppercase" flexWrap="wrap" gap={4}>
                    <Text>{t('landing.footer.copyright')}</Text>
                    <HStack gap={4}>
                        <Link asChild _hover={{ color: '#84C3FF' }}><RouterLink to="/privacy">Privacy</RouterLink></Link>
                        <Link asChild _hover={{ color: '#84C3FF' }}><RouterLink to="/terms">Terms</RouterLink></Link>
                    </HStack>
                </Flex>
            </Container>
        </Box>
    )
}

// ═══════════════════════════════════════════════════════════════
// LANDING PAGE
// ═══════════════════════════════════════════════════════════════
const LandingPage: React.FC = () => {
    const { t } = useTranslation()
    const [params, setParams] = useSearchParams()
    const initial = params.get('audience') === 'c' ? 'c' : 'b'
    const [audience, setAudience] = useState<'b' | 'c'>(initial)
    const isMobile = useBreakpointValue({ base: true, lg: false })

    const setA = (a: 'b' | 'c') => {
        setAudience(a)
        const p = new URLSearchParams(params); p.set('audience', a); setParams(p, { replace: true })
    }

    const headline: string[] = t(audience === 'b' ? 'landing.hero.headlineBarbers' : 'landing.hero.headlineCustomers', { returnObjects: true })
    const sub = t(audience === 'b' ? 'landing.hero.subBarbers' : 'landing.hero.subCustomers')

    return (
        <Box>
            <SEO
                title="BarberMate — Rezervačný systém pre barbershopy | Booking pre barberov"
                description="Rezervačná platforma pre barbershopy na Slovensku a v EÚ. Zákazníci ťa nájdu, rezervujú online a prídu. Bez provízie. 14 dní zadarmo."
                canonical="/"
            />
            {/* ─── HERO ──────────────────────────────────────── */}
            <Box
                as="section"
                position="relative"
                minH={{ base: 'auto', lg: '92vh' }}
                bg="gray.50"
                overflow="hidden"
                pt={{ base: 12, md: 20 }}
                pb={{ base: 16, md: 24 }}
            >
                {/* Soft gradient blobs */}
                <Box position="absolute" top="-10%" right="-10%" w="60vw" h="60vw" borderRadius="full" bg="#D6EBFF" filter="blur(120px)" opacity={0.6} pointerEvents="none" />
                <Box position="absolute" bottom="-20%" left="-10%" w="40vw" h="40vw" borderRadius="full" bg={RED} filter="blur(140px)" opacity={0.10} pointerEvents="none" />

                <Container maxW="1440px" position="relative">
                    <Grid templateColumns={isMobile ? '1fr' : '1.2fr 1fr'} gap={{ base: 10, lg: 16 }} alignItems="center">
                        <GridItem>
                            <VStack align="flex-start" gap={6}>
                                <Text fontFamily={MONO} fontSize="11px" letterSpacing="0.3em" color="gray.500" textTransform="uppercase">
                                    ◆ {t('landing.hero.tag')}
                                </Text>

                                {/* Audience toggle - rounded pill */}
                                <HStack gap={2} bg="white" border="1px solid" borderColor="gray.200" borderRadius="full" p={1}>
                                    {(['b', 'c'] as const).map((a) => (
                                        <Box key={a} as="button" onClick={() => setA(a)}
                                            px={4} py={2} borderRadius="full"
                                            fontFamily={MONO} fontSize="11px" fontWeight={700} letterSpacing="0.2em"
                                            bg={audience === a ? BLUE : 'transparent'}
                                            color={audience === a ? 'white' : 'gray.700'}
                                            transition="all .2s"
                                        >
                                            {t(a === 'b' ? 'landing.hero.audienceBarbers' : 'landing.hero.audienceCustomers')}
                                        </Box>
                                    ))}
                                </HStack>

                                {/* Headline */}
                                <Heading as="h1" fontSize={{ base: '64px', md: '96px', lg: '128px' }} lineHeight={0.95} letterSpacing="-0.03em" fontWeight={800} color="gray.900">
                                    {headline.map((line, i) => (
                                        <Box as="span" display="block" key={`${audience}-${i}`} color={i === headline.length - 1 ? RED : 'inherit'}>{line}</Box>
                                    ))}
                                </Heading>

                                <Text fontSize="18px" color="gray.600" maxW="540px" lineHeight={1.6}>{sub}</Text>

                                {/* CTAs - different for barbers vs customers */}
                                <HStack gap={4} pt={2} flexWrap="wrap">
                                    {audience === 'b' ? (
                                        <>
                                            <Button asChild
                                                onClick={() => analytics.ctaClicked('hero', 'register')}
                                                bg={BLUE} color="white" fontFamily={MONO} fontWeight={700} fontSize="13px" letterSpacing="0.2em"
                                                px={8} py={5} h="auto" borderRadius="md"
                                                boxShadow="0 4px 12px rgba(0,136,255,.3)"
                                                _hover={{ transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,136,255,.4)' }}
                                            >
                                                <RouterLink to="/register">{t('landing.hero.ctaPrimary').toUpperCase()} →</RouterLink>
                                            </Button>
                                            <Button variant="ghost" color="gray.700" fontFamily={MONO} fontWeight={700} fontSize="13px" letterSpacing="0.2em"
                                                px={8} py={5} h="auto" borderRadius="md" _hover={{ color: BLUE, bg: 'transparent' }}
                                                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                                            >
                                                {t('landing.hero.ctaSecondary').toUpperCase()}
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button asChild
                                                onClick={() => analytics.ctaClicked('hero', 'explore')}
                                                bg={BLUE} color="white" fontFamily={MONO} fontWeight={700} fontSize="13px" letterSpacing="0.2em"
                                                px={8} py={5} h="auto" borderRadius="md"
                                                boxShadow="0 4px 12px rgba(0,136,255,.3)"
                                                _hover={{ transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,136,255,.4)' }}
                                            >
                                                <RouterLink to="/explore"><Icon as={FaSearch} mr={2} />{t('landing.customerCta.explore').toUpperCase()} →</RouterLink>
                                            </Button>
                                            <Button asChild variant="ghost" color="gray.700" fontFamily={MONO} fontWeight={700} fontSize="13px" letterSpacing="0.2em"
                                                px={8} py={5} h="auto" borderRadius="md" _hover={{ color: BLUE, bg: 'transparent' }}
                                            >
                                                <RouterLink to="/register">{t('landing.customerCta.register').toUpperCase()}</RouterLink>
                                            </Button>
                                        </>
                                    )}
                                </HStack>

                                {/* HIDDEN_TODO #31 — hero stats row hidden: the numbers
                                    (180+/12k/4.8★) were fabricated; bring back wired to a
                                    real BE stats endpoint. See docs/HIDDEN_TODO.md.
                                <HStack gap={{ base: 8, md: 12 }} pt={8} flexWrap="wrap">
                                    {[
                                        { n: '180+', l: t('landing.hero.stats.shops') },
                                        { n: '12k', l: t('landing.hero.stats.bookings') },
                                        { n: '4.8★', l: t('landing.hero.stats.rating') },
                                    ].map((s) => (
                                        <VStack key={s.l} align="flex-start" gap={1}>
                                            <Text fontWeight={700} fontSize="48px" color={BLUE} lineHeight={1}>{s.n}</Text>
                                            <Text fontFamily={MONO} fontSize="10px" letterSpacing="0.2em" color="gray.500" textTransform="uppercase">{s.l}</Text>
                                        </VStack>
                                    ))}
                                </HStack>
                                */}
                            </VStack>
                        </GridItem>

                        <GridItem>
                            <Box position="relative" display="flex" justifyContent="center" alignItems="center" minH={{ base: 'auto', lg: '580px' }}>
                                {/* 3D Barber Pole — behind demo, pushed to the left */}
                                <Box
                                    display={{ base: 'none', lg: 'block' }}
                                    position="absolute"
                                    top="50%" left="0"
                                    transform="translateY(-50%)"
                                    w="260px" h="520px"
                                    pointerEvents="none"
                                >
                                    <BarberPole3D />
                                </Box>
                                {/* Demo card — centered, wide, 80% opacity */}
                                <Box position="relative" zIndex={1} opacity={0.9} w={{ base: '100%', lg: '100%' }} maxW="460px" mx="auto">
                                    <BookingDemo t={t} />
                                </Box>
                            </Box>
                        </GridItem>
                    </Grid>
                </Container>
            </Box>

            {/* ─── MAP (real data from API) ────────────────── */}
            <MapSection t={t} />

            {/* ─── EXPLORE BANNER (for customers) ────────────── */}
            <ExploreBanner t={t} />

            {/* ─── HOW IT WORKS ──────────────────────────────── */}
            <HowItWorks t={t} />

            {/* ─── PRICING ───────────────────────────────────── */}
            <PricingSection t={t} />

            {/* ─── FINAL CTA ─────────────────────────────────── */}
            <FinalCTA t={t} />

            {/* ─── FOOTER ────────────────────────────────────── */}
            <LandingFooter t={t} />
        </Box>
    )
}

export default LandingPage
